<template>
  <div class="store-pricing-config">
    <div class="page-header">
      <h2>门店价格配置</h2>
      <p>按门店独立维护各设备类型价格和会员充值套餐。</p>
    </div>

    <div class="page-content">
      <div class="store-list-panel">
        <div class="panel-header">
          <h3>门店</h3>
          <button class="text-btn" @click="loadConfigs">刷新</button>
        </div>
        <div v-if="loading" class="empty-state">加载中...</div>
        <button
          v-for="store in storeList"
          v-else
          :key="store.storeId"
          :class="['store-item', selectedStore && selectedStore.storeId === store.storeId ? 'active' : '']"
          @click="selectStore(store)"
        >
          <strong>{{ store.storeName }}</strong>
          <span class="store-code">{{ store.storeCode || '-' }}</span>
        </button>
      </div>

      <div class="config-panel">
        <div v-if="!selectedStore" class="empty-state">请选择左侧门店后维护价格配置</div>
        <form v-else class="config-form" @submit.prevent="saveConfig">
          <div class="form-header">
            <div>
              <h3>{{ selectedStore.storeName }}</h3>
              <p>保存后会同步影响小程序上机档位、后端扣费金额和客户端当前设备类型价目。</p>
            </div>
            <button type="submit" class="save-btn" :disabled="saving">{{ saving ? '保存中...' : '保存价格配置' }}</button>
          </div>

          <section class="section-card">
            <div class="section-header">
              <div>
                <h4>设备类型计费档位</h4>
                <p>所有计时价格都在这里维护；设备类型名称请到“设备管理 > 设备类型配置”维护。</p>
              </div>
              <button type="button" class="secondary-btn" @click="addDeviceTypeDurationRow">添加类型档位</button>
            </div>

            <table class="config-table">
              <thead>
                <tr>
                  <th>设备类型</th>
                  <th>时长（分钟）</th>
                  <th>金额（元）</th>
                  <th>展示文案</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in deviceTypeDurationRows" :key="row.key">
                  <td>
                    <select v-model="row.deviceType" class="table-input">
                      <option v-for="type in deviceTypeOptions" :key="type.code" :value="type.code">
                        {{ type.name }}
                      </option>
                    </select>
                  </td>
                  <td><input v-model.number="row.minutes" type="number" min="1" step="1" class="table-input" /></td>
                  <td><input v-model.number="row.amount" type="number" min="0" step="0.01" class="table-input" /></td>
                  <td>{{ getDeviceTypeName(row.deviceType) }}：{{ formatDuration(row.minutes) }}：{{ formatAmount(row.amount) }}元</td>
                  <td><button type="button" class="danger-btn" @click="removeDeviceTypeDurationRow(index)">删除</button></td>
                </tr>
                <tr v-if="!deviceTypeDurationRows.length">
                  <td colspan="5" class="empty-row">暂无设备类型专属档位，可添加</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section class="section-card">
            <div class="section-header">
              <div>
                <h4>会员充值套餐</h4>
                <p>小程序店员充值页会读取这些固定档位和赠送规则。</p>
              </div>
              <button type="button" class="secondary-btn" @click="addRechargeRow">添加套餐</button>
            </div>

            <table class="config-table">
              <thead>
                <tr>
                  <th>套餐名</th>
                  <th>实付金额（元）</th>
                  <th>赠送余额（元）</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in rechargeRows" :key="row.key">
                  <td><input v-model.trim="row.name" class="table-input" placeholder="如：基础档" /></td>
                  <td><input v-model.number="row.amount" type="number" min="0" step="0.01" class="table-input" /></td>
                  <td><input v-model.number="row.giftAmount" type="number" step="0.01" class="table-input" /></td>
                  <td><button type="button" class="danger-btn" @click="removeRechargeRow(index)">删除</button></td>
                </tr>
                <tr v-if="!rechargeRows.length">
                  <td colspan="5" class="empty-row">暂无充值套餐，可添加固定档位</td>
                </tr>
              </tbody>
            </table>

            <div class="switch-grid">
              <label class="switch-item">
                <input v-model="allowCustomRecharge" type="checkbox" />
                <span>允许店员自定义充值金额</span>
              </label>
            </div>
          </section>

        </form>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../utils/request'

let rowId = 1

export default {
  name: 'StorePricingConfig',
  data() {
    return {
      loading: false,
      saving: false,
      storeList: [],
      selectedStore: null,
      deviceTypeOptions: [],
      deviceTypeDurationRows: [],
      rechargeRows: [],
      allowCustomRecharge: false,
    }
  },
  computed: {
    normalizedDeviceTypeDurationRows() {
      return this.deviceTypeDurationRows
        .map(row => ({
          deviceType: String(row.deviceType || '').trim(),
          minutes: Number(row.minutes || 0),
          amount: Number(row.amount || 0)
        }))
        .filter(row => row.deviceType && row.minutes > 0 && row.amount > 0)
        .sort((a, b) => {
          if (a.deviceType === b.deviceType) return a.minutes - b.minutes
          return Number(a.deviceType || 0) - Number(b.deviceType || 0)
        })
    },
    generatedDeviceTypeDurationRules() {
      return this.normalizedDeviceTypeDurationRows
        .map(row => `${row.deviceType}|${this.trimNumber(row.amount)}|${row.minutes}`)
        .join('\n')
    },
    generatedRechargeAmountOptions() {
      return this.normalizedRechargeRows
        .map(row => this.trimNumber(row.amount))
        .join(',')
    },
    generatedRechargeOptionDetails() {
      return this.normalizedRechargeRows
        .map(row => `${row.name || `${this.trimNumber(row.amount)}元档`}|${this.trimNumber(row.amount)}|${this.trimNumber(row.giftAmount)}|0`)
        .join('\n')
    },
    normalizedRechargeRows() {
      return this.rechargeRows
        .map(row => ({
          name: (row.name || '').trim(),
          amount: Number(row.amount || 0),
          giftAmount: Number(row.giftAmount || 0)
        }))
        .filter(row => Number.isFinite(row.amount) && row.amount >= 0)
        .sort((a, b) => a.amount - b.amount)
    }
  },
  mounted() {
    this.loadConfigs()
  },
  methods: {
    async loadConfigs() {
      this.loading = true
      try {
        const response = await request.get('/api/stores/pricing-configs')
        if (response.data.success && response.data.code === 200) {
          this.storeList = response.data.data || []
          if (!this.selectedStore && this.storeList.length) {
            this.selectStore(this.storeList[0])
          } else if (this.selectedStore) {
            const updated = this.storeList.find(item => item.storeId === this.selectedStore.storeId)
            if (updated) {
              this.selectStore(updated)
            }
          }
        }
      } catch (error) {
        alert(error.message || '加载价格配置失败')
      } finally {
        this.loading = false
      }
    },
    selectStore(store) {
      this.selectedStore = store
      this.deviceTypeDurationRows = this.parseDeviceTypeDurationRows(store.deviceTypeDurationAmountRules)
      this.rechargeRows = this.parseRechargeRows(store.rechargeOptionDetails, store.rechargeAmountOptions)
      this.allowCustomRecharge = !!store.allowCustomRecharge
      this.loadDeviceTypeOptions(store.storeId)
    },
    parseDeviceTypeDurationRows(rawRules) {
      return String(rawRules || '')
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(Boolean)
        .map(line => {
          const parts = line.split('|').map(item => item.trim())
          if (parts.length !== 3) return null
          const deviceType = parts[0]
          const amount = Number(String(parts[1]).replace(/[^\d.]/g, ''))
          const minutes = Number(String(parts[2]).replace(/[^\d]/g, ''))
          if (!deviceType || !Number.isFinite(amount) || amount < 0 || !minutes) return null
          return { key: rowId++, deviceType, minutes, amount }
        })
        .filter(Boolean)
    },
    async loadDeviceTypeOptions(storeId) {
      try {
        const response = await request.get('/api/devices/device-types', {
          params: {
            storeId
          }
        })
        if (response.data.success && response.data.code === 200) {
          this.deviceTypeOptions = response.data.data || []
        } else {
          throw new Error(response.data.message || '获取设备类型失败')
        }
      } catch (error) {
        console.error('获取设备类型失败:', error)
        this.deviceTypeOptions = []
      }
    },
    parseRechargeRows(rawDetails, rawAmounts) {
      const rows = String(rawDetails || '')
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(Boolean)
        .map(line => {
          const parts = line.split('|').map(item => item.trim())
          if (parts.length < 2) return null
          const amount = Number(String(parts[1]).replace(/[^\d.]/g, ''))
          if (!Number.isFinite(amount) || amount < 0) return null
          return {
            key: rowId++,
            name: parts[0],
            amount,
            giftAmount: Number(String(parts[2] || 0).replace(/[^-\d.]/g, '')) || 0
          }
        })
        .filter(Boolean)

      if (rows.length) {
        return rows
      }

      return String(rawAmounts || '')
        .replace(/\r/g, '\n')
        .replace(/[，、]/g, ',')
        .split(/[,\n]+/)
        .map(item => Number(String(item).replace(/[^\d.]/g, '')))
        .filter(amount => Number.isFinite(amount) && amount >= 0)
        .map(amount => ({ key: rowId++, name: `${this.trimNumber(amount)}元档`, amount, giftAmount: 0 }))
    },
    addDeviceTypeDurationRow() {
      if (!this.deviceTypeOptions.length) {
        alert('请先到设备管理 > 设备类型配置中维护设备类型')
        return
      }
      this.deviceTypeDurationRows.push({
        key: rowId++,
        deviceType: this.deviceTypeOptions[0].code,
        minutes: 30,
        amount: 0
      })
    },
    removeDeviceTypeDurationRow(index) {
      this.deviceTypeDurationRows.splice(index, 1)
    },
    addRechargeRow() {
      this.rechargeRows.push({ key: rowId++, name: '', amount: 0, giftAmount: 0 })
    },
    removeRechargeRow(index) {
      this.rechargeRows.splice(index, 1)
    },
    validateConfig() {
      if (!this.deviceTypeOptions.length) {
        throw new Error('请先到设备管理 > 设备类型配置中维护设备类型')
      }

      const rechargeAmounts = new Set()
      const deviceTypeDurationKeys = new Set()
      for (const row of this.deviceTypeDurationRows) {
        if (!row.deviceType || !row.minutes || row.minutes <= 0 || row.amount === '' || row.amount === null || row.amount === undefined || Number(row.amount) <= 0) {
          throw new Error('设备类型计费档位的设备类型、分钟和金额都必须填写，金额必须大于 0')
        }
        const key = `${row.deviceType}-${Number(row.minutes)}`
        if (deviceTypeDurationKeys.has(key)) {
          throw new Error(`${this.getDeviceTypeName(row.deviceType)} 存在重复时长：${row.minutes} 分钟`)
        }
        deviceTypeDurationKeys.add(key)
      }

      for (const row of this.rechargeRows) {
        if (row.amount === '' || row.amount === null || row.amount === undefined || Number(row.amount) < 0) {
          throw new Error('充值套餐金额不能小于 0')
        }
        if (rechargeAmounts.has(Number(row.amount))) {
          throw new Error(`充值套餐存在重复金额：${row.amount} 元`)
        }
        rechargeAmounts.add(Number(row.amount))
      }
    },
    async saveConfig() {
      if (!this.selectedStore) return
      try {
        this.validateConfig()
      } catch (error) {
        alert(error.message)
        return
      }

      this.saving = true
      try {
        const payload = {
          deviceTypeDurationAmountRules: this.generatedDeviceTypeDurationRules,
          rechargeAmountOptions: this.generatedRechargeAmountOptions,
          rechargeOptionDetails: this.generatedRechargeOptionDetails,
          allowCustomRecharge: this.allowCustomRecharge
        }
        const response = await request.put(`/api/stores/${this.selectedStore.storeId}/pricing-config`, payload)
        if (response.data.success && response.data.code === 200) {
          const updated = response.data.data
          const index = this.storeList.findIndex(item => item.storeId === updated.storeId)
          if (index >= 0) {
            this.storeList.splice(index, 1, updated)
          }
          this.selectStore(updated)
          alert('价格配置已保存')
        } else {
          throw new Error(response.data.message || '保存失败')
        }
      } catch (error) {
        alert(error.message || '保存失败')
      } finally {
        this.saving = false
      }
    },
    formatDuration(minutes) {
      const value = Number(minutes || 0)
      return `${value}分钟`
    },
    formatAmount(amount) {
      return this.trimNumber(Number(amount || 0))
    },
    getDeviceTypeName(deviceType) {
      const type = this.deviceTypeOptions.find(item => item.code === deviceType)
      return type ? type.name : `设备类型${deviceType || ''}`
    },
    trimNumber(value) {
      const number = Number(value || 0)
      return Number.isInteger(number) ? String(number) : number.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')
    }
  }
}
</script>

<style scoped>
.page-header { margin-bottom: 24px; }
.page-header h2 { margin: 0 0 8px; color: #333; }
.page-header p { margin: 0; color: #666; }
.page-content { display: grid; grid-template-columns: 280px 1fr; gap: 20px; }
.store-list-panel, .config-panel, .section-card { background: #fff; border-radius: 8px; padding: 20px; }
.panel-header, .form-header, .section-header { display: flex; justify-content: space-between; gap: 16px; align-items: center; margin-bottom: 16px; }
.panel-header h3, .form-header h3, .section-header h4, .section-card h4 { margin: 0; }
.form-header p, .section-header p, .hint { margin: 6px 0 0; color: #666; font-size: 13px; }
.text-btn { background: transparent; border: none; color: #1890ff; cursor: pointer; }
.store-item { display: block; width: 100%; margin-bottom: 10px; padding: 12px; text-align: left; background: #f7f8fa; border: 1px solid transparent; border-radius: 6px; cursor: pointer; overflow: hidden; }
.store-item strong, .store-item span { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.store-item span { margin-top: 4px; color: #999; font-size: 12px; }
.store-item.active { border-color: #1890ff; background: #e6f7ff; }
.empty-state, .empty-row { padding: 32px; text-align: center; color: #999; }
.config-form { display: flex; flex-direction: column; gap: 18px; }
.save-btn, .secondary-btn { border: none; border-radius: 6px; padding: 8px 16px; cursor: pointer; }
.save-btn { background: #1890ff; color: #fff; }
.save-btn:disabled { opacity: 0.65; cursor: not-allowed; }
.secondary-btn { background: #f0f5ff; color: #1d4ed8; }
.danger-btn { border: none; border-radius: 4px; padding: 6px 10px; background: #fff1f0; color: #cf1322; cursor: pointer; }
.config-table { width: 100%; border-collapse: collapse; }
.config-table th, .config-table td { border-bottom: 1px solid #f0f0f0; padding: 10px; text-align: left; }
.config-table th { background: #fafafa; color: #555; }
.table-input, .form-input { width: 100%; height: 34px; padding: 0 10px; border: 1px solid #d9d9d9; border-radius: 6px; box-sizing: border-box; }
.form-textarea { width: 100%; min-height: 120px; padding: 10px 12px; border: 1px solid #d9d9d9; border-radius: 6px; resize: vertical; box-sizing: border-box; }
.fallback-row { margin-top: 16px; max-width: 360px; }
.form-group label { display: block; margin-bottom: 8px; color: #333; }
.switch-grid, .number-grid, .preview-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; margin-top: 16px; }
.switch-item { padding: 12px; background: #f7f8fa; border-radius: 6px; }
@media (max-width: 1100px) {
  .page-content, .switch-grid, .number-grid, .preview-grid { grid-template-columns: 1fr; }
}
</style>
